import { Link } from "react-router-dom";
// hip_number is the permanent competition identifier
import { Heart } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { StatusBadge } from "@/components/StatusBadge";
import { BidNowButton } from "@/components/BidNowButton";

export const MustangCard = ({ horse, index = 0 }) => {
  const { isFav, toggle } = useFavorites();
  const fav = isFav(horse.id);
  const scratched = horse.is_scratched || horse.is_withdrawn;
  const sold = horse.sale_price != null;
  const archived = horse.archived_year != null;
  const inAuction = !archived && horse.auction_status === "In Auction" && !scratched && !sold;
  const place = horse.archive_snapshot?.placing;

  return (
    <div
      className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
    >
      <Link to={`/mustangs/${horse.id}`} data-testid={`mustang-card-${horse.hip_number}`} className="flex flex-1 flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {horse.current_photos?.[0] || horse.intake_photo ? (
            <img
              src={horse.current_photos?.[0] || horse.intake_photo}
              alt={horse.horse_name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
              <span className="font-display text-4xl font-bold text-primary/25">
                HIP #{horse.hip_number}
              </span>
            </div>
          )}
          <button
            data-testid={`fav-btn-${horse.hip_number}`}
            onClick={(e) => { e.preventDefault(); toggle(horse.id); }}
            className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-white/90 backdrop-blur transition-colors hover:bg-white"
            aria-label="Toggle favorite"
          >
            <Heart className="h-4.5 w-4.5" style={{ width: 18, height: 18 }}
              fill={fav ? "hsl(26 50% 51%)" : "none"}
              stroke={fav ? "hsl(26 50% 51%)" : "hsl(218 15% 45%)"} />
          </button>
          <span className="absolute left-2 top-2 rounded-md bg-primary/90 px-2 py-0.5 text-xs font-bold text-primary-foreground">
            HIP #{horse.hip_number}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3">
          <h3 className="font-display text-lg font-bold leading-tight text-primary">
            {horse.horse_name}
          </h3>
          <p className="text-sm text-muted-foreground">{horse.trainer_name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {horse.division}
            </span>
            {horse.trainer_state && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                {horse.trainer_state}
              </span>
            )}
            {archived ? (
              <>
                {place && <span data-testid={`card-placing-${horse.hip_number}`} className="rounded-full bg-secondary/15 px-2 py-0.5 text-[11px] font-bold text-secondary">Placed #{place}{horse.archive_snapshot?.tied ? " (tie)" : ""}</span>}
                {sold && <span data-testid={`card-sale-${horse.hip_number}`} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">Sold ${Number(horse.sale_price).toLocaleString()}</span>}
              </>
            ) : scratched ? <StatusBadge status="Canceled" />
              : sold ? <StatusBadge status="Sold" />
              : inAuction ? <StatusBadge status="In Auction" />
              : <StatusBadge status="Not in Auction" />}
          </div>
        </div>
      </Link>
      {inAuction && horse.auction_url && (
        <div className="px-3 pb-3">
          <BidNowButton url={horse.auction_url} testid={`bid-now-btn-${horse.hip_number}`} className="h-10 text-sm" />
        </div>
      )}
    </div>
  );
};
